import React, { useEffect, useRef } from "react";
import QuestionImageDisplay from "./QuestionImageDisplay";
import MathRenderer from "../MathRenderer";
import MathLiveField from "../MathLiveField";
import MathSymbolKeyboard from "../MathSymbolKeyboard";
import ProbabilisticResponseInput from "./ProbabilisticResponseInput";
import ProbabilisticResponseFeedback from "./ProbabilisticResponseFeedback";
import ProbabilisticCriteriaList from "./ProbabilisticCriteriaList";
import { isProbabilistic } from "../../utils/probabilisticGrading";
import { normalizeMath, evalNumber } from "../../utils/answerMatching";

// ─── Question type constants ──────────────────────────────────────────────────
export const QUESTION_TYPES = {
  MULTIPLE_CHOICE: "multiple_choice",
  NUMERIC_INPUT: "numeric_input",
  TEXT_INPUT: "text_input",
  ESSAY: "essay",
  // fill_in_the_blank implies ordered answers — answers[0] = blank 1, answers[1] = blank 2, etc.
  // Never use .some() grading for this type; use positional matching instead.
  FILL_IN_THE_BLANK: "fill_in_the_blank",
};

const isMultipleChoice = (type) => type === QUESTION_TYPES.MULTIPLE_CHOICE;
const isNumericInput = (type) => type === QUESTION_TYPES.NUMERIC_INPUT;
const isTextInput = (type) => type === QUESTION_TYPES.TEXT_INPUT;
const isEssay = (type) => type === QUESTION_TYPES.ESSAY;
const isFillInTheBlank = (type) => type === QUESTION_TYPES.FILL_IN_THE_BLANK;

// ─── Option button (multiple choice) ─────────────────────────────────────────
const OptionButton = ({
  opt,
  isSelected,
  isLocked,
  hasCommitted,
  correctAnswerText,
  feedback,
  onClick,
}) => {
  const showCorrectHighlight = hasCommitted && opt === correctAnswerText;
  const isIncorrectOpt = feedback === "incorrect" && isSelected;

  let base =
    "w-full text-left border rounded-lg px-4 py-3 text-sm transition-all duration-150 leading-snug";

  if (showCorrectHighlight) base += " border-emerald-500 bg-emerald-50 text-emerald-900";
  else if (isIncorrectOpt) base += " border-red-400 bg-red-50 text-red-900";
  else if (isSelected) base += " border-sky-500 bg-sky-50 text-sky-900";
  else
    base +=
      " border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 text-gray-800";

  if (isLocked) base += " cursor-not-allowed opacity-80";
  else base += " cursor-pointer";

  let dotColor = "bg-gray-300";
  if (showCorrectHighlight) dotColor = "bg-emerald-500";
  else if (isIncorrectOpt) dotColor = "bg-red-400";
  else if (isSelected) dotColor = "bg-sky-500";

  return (
    <button type="button" className={base} disabled={isLocked} onClick={onClick}>
      <span className="flex items-center gap-3">
        <span
          className={`shrink-0 w-2 h-2 rounded-full ${dotColor} transition-colors duration-150`}
        />
        <span>{opt}</span>
      </span>
    </button>
  );
};

// ─── Helpers for displaying correct answers ───────────────────────────────────
// Converts a plain-text DB answer to LaTeX so KaTeX can render it nicely.
const toDisplayLatex = (raw) => {
  if (!raw) return raw;
  return raw
    .replace(
      /(\([^)]+\)|[a-zA-Z0-9]+)\s*\/\s*(\([^)]+\)|[a-zA-Z0-9]+)/g,
      (_, n, d) => {
        const num = n.startsWith("(") && n.endsWith(")") ? n.slice(1, -1) : n;
        const den = d.startsWith("(") && d.endsWith(")") ? d.slice(1, -1) : d;
        return `\\frac{${num}}{${den}}`;
      }
    )
    .replace(/√([a-zA-Z0-9]+)/g, "\\sqrt{$1}")
    .replace(/\^([a-zA-Z0-9]+)/g, "^{$1}");
};

// Match renderer feedback to ModuleQuiz grading rules for blanks
const doesBlankMatchAnswer = (userRaw, answerObj) => {
  if (!answerObj) return false;

  const userVal = normalizeMath(userRaw ?? "");
  const correctVal = normalizeMath(answerObj.content ?? "");
  const tol = parseFloat(answerObj?.tolerance ?? 0);

  const userNum = evalNumber(userVal);
  const correctNum = evalNumber(correctVal);

  if (!isNaN(userNum) && !isNaN(correctNum)) {
    return Math.abs(userNum - correctNum) <= (isNaN(tol) ? 0 : tol);
  }

  if (answerObj?.case_sensitive) {
    return String(userRaw ?? "").trim() === String(answerObj.content ?? "").trim();
  }

  return userVal === correctVal;
};

// ─── Shared MathLive input (numeric + short answer math) ─────────────────────
const MathInput = ({
  value,
  onChange,
  isLocked,
  feedback,
  correctAnswerText,
  hasCommitted,
  onSubmit,
}) => {
  const mathFieldRef = useRef(null);

  let borderColor = "#d1d5db";
  if (feedback === "correct") borderColor = "#34d399";
  if (feedback === "incorrect") borderColor = "#f87171";

  return (
    <div className="space-y-2">
      <div className="w-full max-w-sm">
        <MathLiveField
          ref={mathFieldRef}
          value={value ?? ""}
          onChange={onChange}
          disabled={isLocked}
          onEnter={() => {
            if (!isLocked && value?.toString().trim()) onSubmit?.();
          }}
          borderColor={borderColor}
        />
      </div>

      {!isLocked && (
        <MathSymbolKeyboard
          onSymbolClick={(latex) => mathFieldRef.current?.insertLatex(latex)}
        />
      )}

      {hasCommitted && feedback === "incorrect" && correctAnswerText && (
        <p className="text-xs text-gray-500">
          Correct answer:{" "}
          <span className="font-semibold text-emerald-700">
            <MathRenderer content={`$${toDisplayLatex(correctAnswerText)}$`} />
          </span>
        </p>
      )}
    </div>
  );
};

// ─── Fill in the blank (ordered — answers[i] maps to blank i) ────────────────
// selectedOption is an array of strings, one per blank.
// onInputChange(i, value) updates blank i.
const FillInTheBlank = ({
  question,
  selectedOption,
  onChange,
  isLocked,
  feedback,
  hasCommitted,
  onSubmit,
}) => {
  const refs = useRef([]);
  const activeBlankIdxRef = useRef(0);
  const answers = question?.answers ?? [];

  useEffect(() => {
    refs.current = [];
    activeBlankIdxRef.current = 0;
  }, [question?.id]);

  const setActiveBlank = (idx) => {
    activeBlankIdxRef.current = idx;
  };

  // Per-blank feedback aligned to actual grading logic
  const getBorderColor = (i) => {
    if (!hasCommitted) return "#d1d5db";
    const userVal = (selectedOption ?? [])[i] ?? "";
    const matched = doesBlankMatchAnswer(userVal, answers[i]);
    return matched ? "#34d399" : "#f87171";
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {answers.map((answerObj, i) => (
          <div key={i} className="flex flex-col gap-1">
            <span className="text-xs text-gray-400 font-medium">Blank {i + 1}</span>
            <div
              className="w-36"
              onMouseDown={() => setActiveBlank(i)}
              onTouchStart={() => setActiveBlank(i)}
            >
              <MathLiveField
                ref={(el) => {
                  refs.current[i] = el;
                }}
                value={(selectedOption ?? [])[i] ?? ""}
                onChange={(val) => onChange(i, val)}
                onFocus={() => setActiveBlank(i)}
                disabled={isLocked}
                onEnter={() => {
                  if (i < answers.length - 1) {
                    setActiveBlank(i + 1);
                    refs.current[i + 1]?.focus();
                  } else if (!isLocked) {
                    onSubmit?.();
                  }
                }}
                borderColor={getBorderColor(i)}
              />
            </div>
          </div>
        ))}
      </div>

      {!isLocked && (
        <MathSymbolKeyboard
          onSymbolClick={(latex) => {
            const fallbackIdx = 0;
            const activeIdx =
              Number.isInteger(activeBlankIdxRef.current) &&
              activeBlankIdxRef.current >= 0 &&
              activeBlankIdxRef.current < answers.length
                ? activeBlankIdxRef.current
                : fallbackIdx;

            refs.current[activeIdx]?.insertLatex?.(latex);
          }}
        />
      )}

      {hasCommitted && feedback === "incorrect" && (
        <div className="space-y-1">
          {answers.map((answerObj, i) => (
            <p key={i} className="text-xs text-gray-500">
              Blank {i + 1} answer:{" "}
              <span className="font-semibold text-emerald-700">
                <MathRenderer content={`$${toDisplayLatex(answerObj.content)}$`} />
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Plain text input (biology, history — no math) ───────────────────────────
const PlainTextInput = ({
  value,
  onChange,
  isLocked,
  feedback,
  correctAnswerText,
  hasCommitted,
  onSubmit,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isLocked && inputRef.current) inputRef.current.focus();
  }, [isLocked]);

  let borderColor = "border-gray-300 focus:border-sky-400 focus:ring-sky-100";
  if (feedback === "correct")
    borderColor = "border-emerald-400 focus:border-emerald-400 focus:ring-emerald-100";
  if (feedback === "incorrect")
    borderColor = "border-red-400   focus:border-red-400   focus:ring-red-100";

  return (
    <div className="space-y-2">
      <div className="w-full max-w-sm">
        <textarea
          ref={inputRef}
          rows={1}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isLocked && value?.trim()) {
              e.preventDefault();
              onSubmit?.();
            }
          }}
          disabled={isLocked}
          placeholder="Type your answer…"
          className={`w-full rounded-lg border px-4 py-2 text-sm text-gray-800 leading-relaxed
            bg-white outline-none ring-2 ring-transparent transition-all duration-150 resize-none
            disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-500
            ${borderColor}`}
        />
        {hasCommitted && feedback === "incorrect" && correctAnswerText && (
          <p className="text-xs text-gray-500">
            Correct answer:{" "}
            <span className="font-semibold text-emerald-700">{correctAnswerText}</span>
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Multi-part card (groups sub-questions under one parent) ─────────────────
// Each sub-part is independently gradable — its own Check button, feedback, and
// explanation. The card is a visual container; navigation (Next/Skip) is handled
// by the parent ModuleQuiz at the card level.
export const MultiPartCard = ({
  questions,
  indices,
  parentNumber,
  selectedByIdx,
  feedbackByIdx,
  committedCheckByIdx,
  lockedByIdx,
  showHintByIdx,
  shuffledOptionsByFlatIdx,
  onCheck,
  onInputChange,
  onFillBlankChange,
  onToggleHelp,
  gradingByIdx,     // open-ended only, keyed by flat index
  onRetryGrade,
}) => {
  // Derive sub-label from question_number e.g. "3a" → "(a)", "3b_i" → "(b)(i)"
  const subLabel = (qNum) => {
    if (!parentNumber || !qNum) return qNum;
    const sub = qNum.replace(new RegExp(`^${parentNumber}`), "");
    return sub.split("_").map((p) => `(${p})`).join("");
  };

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/40 overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-sky-100/60 border-b border-sky-200">
        <span className="text-xs font-bold text-sky-700 uppercase tracking-wider">
          Question {parentNumber}
        </span>
        <span className="text-xs text-sky-500 font-medium">· Multi-part</span>
      </div>

      {/* Sub-parts */}
      <div className="divide-y divide-sky-100">
        {questions.map((q, i) => {
          const flatIdx = indices[i];
          const qType = q.question_type ?? QUESTION_TYPES.MULTIPLE_CHOICE;
          const sel = selectedByIdx[flatIdx] ?? null;
          const fb = feedbackByIdx[flatIdx] ?? null;
          const committed = committedCheckByIdx[flatIdx] ?? false;
          const locked = lockedByIdx[flatIdx] ?? false;
          const hintOpen = showHintByIdx[flatIdx] ?? false;
          const correctAnswerObj = q.answers?.find((a) => a.is_correct) ?? q.answers?.[0];
          const correctAnswerText = correctAnswerObj?.content;
          const explanation = correctAnswerObj?.explanation;
          const shuffled = shuffledOptionsByFlatIdx?.[flatIdx] ?? q.options ?? [];

          const openEnded = isProbabilistic(q);
          const gradingState = gradingByIdx?.[flatIdx];

          const subDisabled = (() => {
            if (committed || locked) return true;
            if (gradingState?.state === "grading") return true;
            // Open-ended questions carry no answer text — the mark scheme stays on the
            // server — so only require that something has been written.
            if (openEnded) return !sel?.toString().trim();
            if (qType === "fill_in_the_blank") {
              const blanks = q.answers?.length ?? 0;
              const arr = Array.isArray(sel) ? sel : [];
              return blanks === 0 || !arr.slice(0, blanks).every((v) => v?.toString().trim());
            }
            return !sel?.toString().trim() || !correctAnswerText;
          })();

          return (
            <div key={flatIdx} className="px-4 py-4 space-y-3">
              {/* Sub-part label + question text
                  If content has a \n\n, the part before is a shared preamble
                  rendered above the label; the part after sits inline with (a)/(b) etc. */}
              {(() => {
                const contentParts = q.content.split(/\n\n+/);
                const preamble =
                  contentParts.length > 1 ? contentParts.slice(0, -1).join("\n\n") : null;
                const equation = contentParts[contentParts.length - 1];
                return (
                  <>
                    {preamble && (
                      <p className="text-[14px] font-semibold text-gray-800 leading-relaxed">
                        <MathRenderer content={preamble} />
                      </p>
                    )}
                    <div className="flex items-start gap-2">
                      <span className="shrink-0 mt-0.5 text-xs font-bold text-sky-600 whitespace-nowrap">
                        {subLabel(q.question_number)}
                      </span>
                      <p className="text-[14px] font-semibold text-gray-800 leading-relaxed">
                        <MathRenderer content={equation} />
                      </p>
                    </div>
                  </>
                );
              })()}

              {/* Image — aligned with input under the sub-label */}
              {q.has_image && (
                <div className="pl-8">
                  <QuestionImageDisplay questionId={q.id} align="left" />
                </div>
              )}

              {/* Input */}
              <div className="pl-8">
                {openEnded ? (
                  <div className="space-y-3">
                    <ProbabilisticResponseInput
                      value={sel}
                      onChange={(val) => onInputChange(flatIdx, val)}
                      isLocked={locked}
                      outcome={fb}
                      maxMarks={q.max_marks}
                      isGrading={gradingState?.state === "grading"}
                    />
                    {gradingState && (
                      <ProbabilisticResponseFeedback
                        {...gradingState}
                        onRetry={() => onRetryGrade?.(flatIdx)}
                      />
                    )}
                  </div>
                ) : isNumericInput(qType) ? (
                  <MathInput
                    value={sel}
                    onChange={(val) => onInputChange(flatIdx, val)}
                    isLocked={locked}
                    feedback={fb}
                    correctAnswerText={correctAnswerText}
                    hasCommitted={committed}
                    onSubmit={() => !subDisabled && onCheck(flatIdx)}
                  />
                ) : isTextInput(qType) || isEssay(qType) ? (
                  <PlainTextInput
                    value={sel}
                    onChange={(val) => onInputChange(flatIdx, val)}
                    isLocked={locked}
                    feedback={fb}
                    correctAnswerText={correctAnswerText}
                    hasCommitted={committed}
                    onSubmit={() => !subDisabled && onCheck(flatIdx)}
                  />
                ) : isFillInTheBlank(qType) ? (
                  <FillInTheBlank
                    question={q}
                    selectedOption={sel}
                    onChange={(blankIdx, val) => onFillBlankChange(flatIdx, blankIdx, val)}
                    isLocked={locked}
                    feedback={fb}
                    hasCommitted={committed}
                    onSubmit={() => !subDisabled && onCheck(flatIdx)}
                  />
                ) : (
                  <div className="space-y-2">
                    {shuffled.map((opt) => (
                      <OptionButton
                        key={opt}
                        opt={opt}
                        isSelected={sel === opt}
                        isLocked={locked}
                        hasCommitted={committed}
                        correctAnswerText={correctAnswerText}
                        feedback={fb}
                        onClick={() => {
                          if (!locked) onInputChange(flatIdx, opt);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Explanation */}
              {hintOpen && explanation && (
                <div
                  className={`ml-8 rounded-lg border px-3 py-2.5 text-sm leading-relaxed ${
                    fb === "correct"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : fb === "incorrect"
                      ? "bg-amber-50 border-amber-200 text-amber-900"
                      : "bg-slate-50 border-slate-200 text-slate-700"
                  }`}
                >
                  <p
                    className={`text-xs font-bold uppercase tracking-wider mb-1 ${
                      fb === "correct"
                        ? "text-emerald-600"
                        : fb === "incorrect"
                        ? "text-amber-600"
                        : "text-slate-400"
                    }`}
                  >
                    {fb === "incorrect" ? "Not quite — here's why" : "Explanation"}
                  </p>
                  <p className="whitespace-pre-line">
                    <MathRenderer content={explanation} />
                  </p>
                  <ProbabilisticCriteriaList criteria={gradingState?.criteria} />
                </div>
              )}

              {/* Per-sub-part actions */}
              <div className="pl-8 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onToggleHelp(flatIdx)}
                  className="text-xs font-semibold text-[#1896b9] hover:text-[#064759] underline"
                >
                  {hintOpen ? "Hide Explanation" : "Show Explanation"}
                </button>

                {!committed && (
                  <button
                    type="button"
                    onClick={() => onCheck(flatIdx)}
                    disabled={subDisabled}
                    className={`ml-auto rounded-md px-4 py-1.5 text-xs font-semibold text-white transition ${
                      subDisabled
                        ? "bg-[#669999] cursor-not-allowed"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                  >
                    Check
                  </button>
                )}

                {committed && (
                  <span
                    className={`ml-auto text-xs font-semibold ${
                      fb === "correct" ? "text-emerald-600" : "text-red-500"
                    }`}
                  >
                    {fb === "correct" ? "✓ Correct" : "✗ Incorrect"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const QuestionRenderer = ({
  question,
  selectedOption,
  feedback,
  hasCommitted,
  isLocked,
  correctAnswerText,
  shuffledOptions,
  onOptionClick,
  onInputChange,
  onFillBlankChange,
  onInputSubmit,
  grading,        // open-ended only: { state, outcome, marksAwarded, maxMarks, criteria, feedback, ... }
  onRetryGrade,
}) => {
  if (!question) return null;

  const qType = question.question_type ?? QUESTION_TYPES.MULTIPLE_CHOICE;

  const isMC = isMultipleChoice(qType);

  return (
    <div className="space-y-4">
      {isMC ? (
        <>
          <p className="text-[15px] font-semibold text-gray-800 leading-relaxed">
            <MathRenderer content={question.content} />
          </p>

          {question.has_image && <QuestionImageDisplay questionId={question.id} />}

          <div className="space-y-2">
            {(shuffledOptions ?? question.options ?? []).map((opt) => (
              <OptionButton
                key={opt}
                opt={opt}
                isSelected={selectedOption === opt}
                isLocked={isLocked}
                hasCommitted={hasCommitted}
                correctAnswerText={correctAnswerText}
                feedback={feedback}
                onClick={() => {
                  if (!isLocked) onOptionClick?.(opt);
                }}
              />
            ))}
          </div>
        </>
      ) : (
        // Open-ended answers need room to write and room for the marked feedback, so
        // they fill the card; short answers stay narrow.
        <div className={`${isProbabilistic(question) ? "w-full" : "max-w-sm"} space-y-4`}>
          <p className="text-[15px] font-semibold text-gray-800 leading-relaxed">
            <MathRenderer content={question.content} />
          </p>

          {question.has_image && (
            <QuestionImageDisplay questionId={question.id} align="left" />
          )}

          {/* Open-ended answers are marked by AI on the server, so this branch comes
              before the question_type checks — an open-ended question is still text_input. */}
          {isProbabilistic(question) ? (
            <>
              <ProbabilisticResponseInput
                value={selectedOption}
                onChange={onInputChange}
                isLocked={isLocked}
                outcome={feedback}
                maxMarks={question.max_marks}
                isGrading={grading?.state === "grading"}
              />
              {grading && <ProbabilisticResponseFeedback {...grading} onRetry={onRetryGrade} />}
            </>
          ) : isNumericInput(qType) ? (
            <MathInput
              value={selectedOption}
              onChange={onInputChange}
              isLocked={isLocked}
              feedback={feedback}
              correctAnswerText={correctAnswerText}
              hasCommitted={hasCommitted}
              onSubmit={onInputSubmit}
            />
          ) : isTextInput(qType) ? (
            <PlainTextInput
              value={selectedOption}
              onChange={onInputChange}
              isLocked={isLocked}
              feedback={feedback}
              correctAnswerText={correctAnswerText}
              hasCommitted={hasCommitted}
              onSubmit={onInputSubmit}
            />
          ) : isFillInTheBlank(qType) ? (
            <FillInTheBlank
              question={question}
              selectedOption={selectedOption}
              onChange={onFillBlankChange}
              isLocked={isLocked}
              feedback={feedback}
              hasCommitted={hasCommitted}
              onSubmit={onInputSubmit}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default QuestionRenderer;